import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import type {GuideIconReaction} from "@khan/guide-utils";

import {GuideIcon32} from "./guide-icon-32.tsx";
import {MessageIconReaction} from "./message-icon-reaction.tsx";

interface Props {
    /** ID for the message icon */
    id: string;
    /** Whether the guide is available to chat. */
    isOffline: boolean;
    /** The reaction associated with the message Icon. */
    reaction: GuideIconReaction | null;
    /** Optional style overrides. */
    style?: StyleType;
}

/**
 * Renders an in-chat avatar used in both Guide Activities & companion mode.
 */
export const MessageIcon = (props: Props): React.ReactElement => {
    const {id, reaction, style, isOffline} = props;

    // Override swagId to use the offline icon if the guide is offline.
    const swagId = isOffline ? "offline" : id;
    return (
        <View
            role="img"
            // Since the icons are reused in various contexts, we don't want
            // to prescribe a semantic meaning to them, and they're just for
            // visual variety. So, we hide the icon from screen readers.
            aria-label=""
            style={[styles.iconWrapper, style]}
            testId="message-icon"
        >
            {reaction && !isOffline ? (
                <MessageIconReaction id={reaction} style={styles.reaction} />
            ) : (
                <GuideIcon32 id={swagId} style={styles.icon} />
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    iconWrapper: {
        flexDirection: "row",
        flex: "0 0 auto",
    },
    icon: {
        width: "100%",
        height: "auto",
        // Apply negative margins to have Khanmigo's body align with the
        // top & left bounds. This allows it so that Khanmigo doesn't shift
        // around when switching between different appearances.
        marginBlockStart: "-45%",
        marginInlineStart: "-20%",
    },
    reaction: {
        // HACK: The following styles are to normalize the reaction icons
        // and make them consistent with the original message icons. Ideally,
        // the reaction icons are consistent with the icons. Re-visit this
        // when we have reactions for Khanmigo + hats.
        transformOrigin: "top left",
        scale: "85%",
        width: "100%",
        height: "auto",
        marginBlockStart: "-15%",
        marginInlineStart: "-12%",
    },
});
